import { Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  onRunMonitor: () => void;
  isRunning: boolean;
  recentCount: number;
  highCount: number;
  midCount: number;
  lowCount: number;
  hasMonitorRun: boolean;
  lastRunCount: number;
  lastRunHighCount: number;
  lastRunMidCount: number;
  lastRunLowCount: number;
  untriagedCount: number;
  onOpenTriageInbox: () => void;
}

function ScoreBreakdown({ highCount, midCount, lowCount }: { highCount: number; midCount: number; lowCount: number }) {
  return (
    <div className="flex gap-3 text-xs text-muted-foreground">
      {highCount > 0 && (
        <span className="flex items-center gap-1">
          <span className="inline-block size-2 rounded-full bg-emerald-500" />
          {highCount} high
        </span>
      )}
      {midCount > 0 && (
        <span className="flex items-center gap-1">
          <span className="inline-block size-2 rounded-full bg-yellow-500" />
          {midCount} medium
        </span>
      )}
      {lowCount > 0 && (
        <span className="flex items-center gap-1">
          <span className="inline-block size-2 rounded-full bg-zinc-500" />
          {lowCount} low
        </span>
      )}
    </div>
  );
}

export function MonitorDigest({ onRunMonitor, isRunning, recentCount, highCount, midCount, lowCount, hasMonitorRun, lastRunCount, lastRunHighCount, lastRunMidCount, lastRunLowCount, untriagedCount, onOpenTriageInbox }: Props) {

  const hasRecent = recentCount > 0;

  return (
    <div className="space-y-3">
      {hasMonitorRun || hasRecent ? (
        <div className="space-y-3">
          {/* Last run section */}
          {hasMonitorRun && (
            <div className="space-y-1.5">
              <p className="text-sm text-muted-foreground">
                {lastRunCount} paper{lastRunCount !== 1 ? "s" : ""} from last run
              </p>
              {lastRunCount > 0 && (
                <ScoreBreakdown highCount={lastRunHighCount} midCount={lastRunMidCount} lowCount={lastRunLowCount} />
              )}
            </div>
          )}

          {/* 7-day section — show if different from last-run count */}
          {hasRecent && recentCount !== lastRunCount && (
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground/70">
                {recentCount} total in the last 7 days
              </p>
              {!hasMonitorRun && (
                <ScoreBreakdown highCount={highCount} midCount={midCount} lowCount={lowCount} />
              )}
            </div>
          )}

          {/* Fallback: 7-day only when monitor has never run */}
          {hasRecent && !hasMonitorRun && recentCount === lastRunCount && (
            <div className="space-y-1.5">
              <p className="text-sm text-muted-foreground">
                {recentCount} paper{recentCount !== 1 ? "s" : ""} discovered in the last 7 days
              </p>
              <ScoreBreakdown highCount={highCount} midCount={midCount} lowCount={lowCount} />
            </div>
          )}

          {/* Triage count */}
          <div className="flex gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <span className="inline-block size-2 rounded-full bg-green-400" />
              {untriagedCount} to triage
            </span>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          No recently discovered papers.
        </p>
      )}

      {/* Triage button */}
      {untriagedCount > 0 && (
        <Button
          variant="default"
          size="sm"
          className="w-full"
          onClick={onOpenTriageInbox}
        >
          <Inbox className="mr-1.5 size-3.5" />
          Review {untriagedCount} paper{untriagedCount !== 1 ? "s" : ""}
        </Button>
      )}

      {/* Run monitor */}
      {untriagedCount === 0 && (
        <button
          className="w-full text-center text-sm text-primary underline-offset-4 hover:underline disabled:opacity-50"
          onClick={onRunMonitor}
          disabled={isRunning}
        >
          {isRunning ? "Monitor running..." : "Run monitor to discover new papers"}
        </button>
      )}
    </div>
  );
}
